import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { SelectOption, SelectOptionProps } from '../SelectOption'
import { SingleValue } from '../types'

export const useSelectSingle = ({
  defaultValue,
  value,
  onChange,
  children,
}: {
  children: ReactNode
  defaultValue?: SingleValue
  value?: SingleValue
  onChange?: (value: SingleValue) => void
}) => {
  const isControlled = value !== undefined

  const { options, optionIds } = useMemo(() => {
    const optionsArray: React.ReactElement<SelectOptionProps>[] = []
    const idsArray: string[] = []
    React.Children.forEach(children, (child) => {
      if (React.isValidElement<SelectOptionProps>(child) && child.type === SelectOption) {
        optionsArray.push(child)
        idsArray.push(child.props.id)
      }
    })
    return { options: optionsArray, optionIds: idsArray }
  }, [children])

  const [selectedIndex, setSelectedIndex] = useState<number | null>(() => {
    if (defaultValue !== undefined) {
      return optionIds.findIndex((id) => id === defaultValue)
    }
    return null
  })

  // Update selected index when value changes
  useEffect(() => {
    if (!isControlled) return
    const index = optionIds.findIndex((id) => id === value)
    if (index === -1) {
      setSelectedIndex(null)
    } else {
      setSelectedIndex(index)
    }
  }, [value, optionIds, isControlled, setSelectedIndex])

  const selectedChildren = useMemo(() => {
    if (selectedIndex == null) return null
    const optionsProps = options[selectedIndex]?.props
    if (!optionsProps) return null
    return {
      children: optionsProps.children,
      icon: optionsProps.icon,
    }
  }, [selectedIndex, options])

  const handleSelect = useCallback(
    (index: number) => {
      if (!isControlled) setSelectedIndex(index)
      onChange?.(optionIds[index])
    },
    [isControlled, onChange, optionIds],
  )

  return {
    selectedIndex,
    handleSelect,
    selectedChildren,
  }
}
